import { useTranslation } from "@opendash/i18n";
import { Button, Card, Flex, StepProps, Steps } from "antd";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";
import { SaveTicketValues } from "../../..";
import { useSaveConfigManager } from "../../hooks/useSaveConfigManager";
import { AssignmentInput } from "../inputs/AssignmentInput";
import { DescriptionInput } from "../inputs/DescriptionInput";
import { DowntimeInputItem } from "../inputs/DowntimeInputItem";
import { DuedateInput } from "../inputs/DuedateInput";
import { FrequencyInputItem } from "../inputs/FrequencyInputItem";
import { IssueInput } from "../inputs/IssueInput";
import { MaterialInputItem } from "../inputs/MaterialInputItem";
import { MediaInputItem } from "../inputs/MediaInputItem";
import { MetaInput } from "../inputs/MetaInput";
import { PriorityInput } from "../inputs/PriorityInput";
import { ProjectInput } from "../inputs/ProjectInput";
import { RestrictionInputItem } from "../inputs/RestrictionInputItem";
import { SourceInput } from "../inputs/SourceInput";
import { StateInput } from "../inputs/StateInput";
import { SubmitButton } from "../inputs/SubmitButton";
import { TitleInput } from "../inputs/TitleInput";

type Props = {};

type StepParams = Array<
  StepProps & {
    component: React.ReactNode;
  }
>;

type StepMap = Map<string, StepProps & { component: React.ReactNode }>;

export const StepsView = observer((props: Props) => {
  const [step, setStep] = useState<number>(0);
  const t = useTranslation();
  const form = useFormInstance<SaveTicketValues>();
  const [errors, setErrors] = useState<string[]>([]);
  const manager = useSaveConfigManager();
  const configs = manager.getAllFieldConfigs();

  async function checkForErrors() {
    form
      .validateFields()
      .then(() => setErrors([]))
      .catch((err) => {
        const errorFields: string[] = err?.errorFields?.map(
          (field) => field.name
        );
        setErrors(errorFields.flat());
      });
  }

  const steps: StepParams = useMemo(() => {
    const stepMap: StepMap = new Map();

    if (
      !configs.title ||
      !configs.title?.hidden ||
      !configs.description ||
      !configs.description?.hidden
    ) {
      // add title step
      // add description step
      stepMap.set("title", {
        title: t("maintenance:ticket.create.title.label"),
        component: (
          <>
            {!configs.title || !configs.title?.hidden ? <TitleInput /> : null}
            {!configs.description || !configs.description?.hidden ? (
              <DescriptionInput />
            ) : null}
          </>
        ),
        status:
          (errors.includes("title") ? "error" : undefined) ||
          (errors.includes("message") ? "error" : undefined),
      });
    }

    if (!configs.state || !configs.state?.hidden) {
      // add state step
      stepMap.set("state", {
        title: t("maintenance:ticket.create.state.label"),
        component: <StateInput type="select" />,
        status: errors.includes("state") ? "error" : undefined,
      });
    }

    if (!configs.source || !configs.source?.hidden) {
      // add source step
      stepMap.set("source", {
        title: t("maintenance:ticket.create.source.label"),
        component: <SourceInput type="dropdown" />,
        status: errors.includes("source") ? "error" : undefined,
      });
    }

    if (!configs.assignedTo || !configs.assignedTo?.hidden) {
      // add assignedTo step
      stepMap.set("assignedTo", {
        title: t("maintenance:ticket.create.assignedTo.label"),
        component: <AssignmentInput type="dropdown" />,
        status: errors.includes("assignedTo") ? "error" : undefined,
      });
    }

    // add steps based on configs
    if (!configs.priority || !configs.priority?.hidden)
      stepMap.set("priority", {
        title: t("maintenance:ticket.create.priority.label"),
        component: (
          <>
            <PriorityInput type="dropdown" />
            <RestrictionInputItem type="dropdown" />
            <FrequencyInputItem type="dropdown" />
            <DowntimeInputItem type="dropdown" />
          </>
        ),
        status: errors.includes("priority") ? "error" : undefined,
      });

    if (!configs.issuecategory || !configs.issuecategory?.hidden)
      stepMap.set("issuecategory", {
        title: t("maintenance:ticket.create.issue.label"),
        component: <IssueInput />,
        status: errors.includes("issue") ? "error" : undefined,
      });

    if (!configs.project || !configs.project?.hidden)
      stepMap.set("project", {
        title: t("maintenance:ticket.create.project.label"),
        component: <ProjectInput type="dropdown" />,
        status: errors.includes("project") ? "error" : undefined,
      });

    if (!configs.material || !configs.material?.hidden)
      stepMap.set("material", {
        title: t("maintenance:ticket.create.material.label"),
        component: <MaterialInputItem type="dropdown" />,
        status: errors.includes("material") ? "error" : undefined,
      });

    if (!configs.media || !configs.media?.hidden)
      stepMap.set("media", {
        title: t("maintenance:ticket.create.media.label"),
        component: <MediaInputItem type="dropdown" />,
        status: errors.includes("media") ? "error" : undefined,
      });

    if (!configs.duedate || !configs.duedate?.hidden)
      stepMap.set("duedate", {
        title: t("maintenance:ticket.create.duedate.label"),
        component: <DuedateInput type="range" />,
        status: errors.includes("duedate") ? "error" : undefined,
      });

    if (!configs.meta || !configs.meta?.hidden) {
      stepMap.set("meta", {
        title: t("maintenance:ticket.create.meta.label"),
        component: <MetaInput type="modal" />,
        status: errors.includes("meta") ? "error" : undefined,
      });
    }

    //Sort stepMap by keys based on the order of the keys in the config variable (Use the title attribute of config and the key of stepMap)
    const sortedKeys = Object.keys(configs).sort((a, b) => {
      const aIndex = Object.keys(configs).indexOf(a);
      const bIndex = Object.keys(configs).indexOf(b);
      return aIndex - bIndex;
    });

    const sortedStepMap: StepMap = new Map();

    sortedKeys.forEach((key) => {
      if (stepMap.has(key)) {
        sortedStepMap.set(key, stepMap.get(key)!);
      }
    });

    // Add any remaining steps that were not in the configs
    stepMap.forEach((value, key) => {
      if (!sortedStepMap.has(key)) {
        sortedStepMap.set(key, value);
      }
    });

    const keys = Array.from(sortedStepMap.keys());
    sortedStepMap.forEach((value, key) => {
      value.onClick = () => setStep(keys.indexOf(key));
    });

    return Array.from(sortedStepMap).map((value) => {
      return {
        title: value[1].title,
        component: value[1].component,
        status: value[1].status,
        onClick: value[1].onClick,
      };
    });
  }, [errors]);

  return (
    <Card
      title={<Steps current={step} onChange={setStep} items={steps} />}
      actions={[
        <Flex
          gap="small"
          justify="flex-start"
          align="center"
          style={{ marginLeft: 12 }}
        >
          <Button
            key="back"
            onClick={() => setStep((s) => s - 1)}
            disabled={step === 0}
          >
            {t("maintenance:global.button.back_label")}
          </Button>
        </Flex>,
        <Flex
          gap="small"
          justify="flex-end"
          align="center"
          style={{ marginRight: 12 }}
        >
          <Button
            key="next"
            onClick={() => setStep((s) => s + 1)}
            disabled={step === steps.length - 1}
          >
            {t("maintenance:global.button.continue_label")}
          </Button>
          <SubmitButton onClick={checkForErrors} />
        </Flex>,
      ]}
    >
      {steps.map((s, i) => {
        return (
          <div key={i} style={{ display: step === i ? "block" : "none" }}>
            {s.component}
          </div>
        );
      })}
    </Card>
  );
});
